import './OverlappingDayCalendarView.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
    MeetingFormat,
    MeetingFormatTypesOptions,
} from '../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { MeetingParticipantsType } from '../../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faCheck,
    faTimes,
    faCalendar,
} from '@fortawesome/free-solid-svg-icons';
import { faCommentDots, faUser } from '@fortawesome/free-regular-svg-icons';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import { CalendarObject } from '../../../../slices/marketplace/meetings/models/calendar-object.model';
import { AvailabilityBlockObject } from '../../../../slices/marketplace/meetings/models/availability-block-object.model';
import { MeetingObject } from '../../../../slices/marketplace/meetings/models/meeting-object.model';
import { MeetingProposalStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-proposal-status.enum';
import { EngagementMeetingStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import InPersonMeetingParticipantsModal from '../InPersonMeetingParticipantsModal/InPersonMeetingParticipantsModal';
import { useNavigate } from 'react-router-dom';
import { QueryParams } from '../../../../utils/queryParams';
import { MarketplaceCommsHubTab } from '../../../../containers/AdminMarketplace/MarketplaceTabs/MarketplaceCommsHub/MarketplaceCommsHubTabs';
import { SellerMarketplaceTab } from '../../../../slices/marketplace/timeline/models/enums/seller-marketplace-tab.enum';
import { BuyerOppMarketplaceTab } from '../../../../slices/marketplace/timeline/models/enums/buyer-opportunity-details-tab.enum';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { useStateSelector } from '../../../../store/selectors';
import { USER_ROLES } from '../../../../utils/constants';
import { EngagementProcessStatus } from '../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import {
    canBuyerTeamMemberManageMeetings,
    canSellerTeamMemberManageMeetings,
    isSellerTeamMemberForEngagement,
} from '../../../../utils/meetingSchedulingHelper';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import EngagementMeetingFullInfoModal from './EngagementMeetingFullInfoModal/EngagementMeetingFullInfoModal';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalHeader, ModalBody } from 'reactstrap';

const HOUR_HEIGHT_PX = 120;
const TOTAL_HOURS = 24;
const TOTAL_HEIGHT_PX = TOTAL_HOURS * HOUR_HEIGHT_PX;

interface TimeSlot {
    hour: number;
    label: string;
}

export interface OverlappingDayCalendarViewProps {
    isVisible: boolean;
    onClose: () => void;
    date: Date;
    events: CalendarObject[];
    selectedEvent?: CalendarObject;
    setSelectedEvent?: React.Dispatch<
        React.SetStateAction<CalendarObject | undefined>
    >;
    highlightedMeetingId?: number;
    viewMode: 'buyerTopLevel' | 'sellerTopLevel' | 'engagement' | 'opportunity';
    party?: TimelineParty;
    engagementId?: number;
    onEditClick?: (eventIdOrProposalId: number | string) => void;
    onProposalAccept?: (meetingProposalId: string) => Promise<void>;
    onProposalCancel?: (meetingProposalId: string) => Promise<void>;
    onRefreshMeetings?: () => void;
    getNotificationFlagCodeForEvent?: (
        event: MeetingObject
    ) => string | undefined;
    selectedBuyers?: EngagementBuyer[];
    practiceName?: string;
}

function isAvailabilityBlockObject(
    obj: CalendarObject
): obj is AvailabilityBlockObject {
    const a = obj as any;
    const formats = a?.formats ?? a?.Formats;
    return Array.isArray(formats);
}

function isOtherGhostMeetingObject(obj: CalendarObject): boolean {
    if (isAvailabilityBlockObject(obj)) {
        return false;
    }
    const meeting = obj as MeetingObject;
    return meeting.isOtherGhostMeeting === true;
}

const computeColumnLayout = (events: CalendarObject[]) => {
    const nonAvailabilityEvents = events.filter(
        (e) => !isAvailabilityBlockObject(e)
    );

    const sorted = [...nonAvailabilityEvents].sort((a, b) => {
        const diff = moment(a.dateStart).diff(moment(b.dateStart));
        return diff !== 0 ? diff : a.id - b.id;
    });

    const eventCol = new Map<CalendarObject, number>();
    const columns: CalendarObject[][] = [];

    for (const evt of sorted) {
        const start = moment(evt.dateStart);
        const end = moment(evt.dateEnd);

        let col = -1;
        for (let i = 0; i < columns.length; i++) {
            const hasOverlap = columns[i].some((e) => {
                return (
                    moment(e.dateStart).isBefore(end) &&
                    moment(e.dateEnd).isAfter(start)
                );
            });
            if (!hasOverlap) {
                col = i;
                break;
            }
        }
        if (col === -1) {
            col = columns.length;
            columns.push([]);
        }
        columns[col].push(evt);
        eventCol.set(evt, col);
    }

    return { eventCol, totalColumns: Math.max(columns.length, 1) };
};

export const OverlappingDayCalendarView = (
    props: OverlappingDayCalendarViewProps
) => {
    const navigate = useNavigate();
    const party = useCurrentMarketplaceParty(props.engagementId);
    const summary = useStateSelector((state) => state.userSummary.summary);
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
    const isBroker = summary?.isMarketplaceBroker;
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const isEngagementLocked = (() => {
        const status = currentEngagement?.engagementProcess?.status;
        return (
            status === EngagementProcessStatus.Suspended ||
            status === EngagementProcessStatus.Terminated ||
            status === EngagementProcessStatus.Completed
        );
    })();

    const hasMeetingStarted = (event: CalendarObject): boolean => {
        if (!event?.dateStart) return false;
        const meetingStartTime =
            event.dateStart instanceof Date
                ? event.dateStart
                : new Date(event.dateStart);
        return meetingStartTime <= new Date();
    };

    const [showApproveConfirmation, setShowApproveConfirmation] =
        useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [selectedMeetingForFullInfo, setSelectedMeetingForFullInfo] =
        useState<MeetingObject | null>(null);
    const [pendingProposalId, setPendingProposalId] = useState<
        string | undefined
    >(undefined);
    const [isApproving, setIsApproving] = useState<boolean>(false);
    const [isCancelling, setIsCancelling] = useState<boolean>(false);
    const [isParticipantsModalVisible, setIsParticipantsModalVisible] =
        useState<boolean>(false);
    const [selectedMeetingForParticipants, setSelectedMeetingForParticipants] =
        useState<MeetingObject | null>(null);

    const activeBuyers = useMemo(() => {
        const baseBuyers = props.selectedBuyers || [];
        const engagementBuyers =
            currentEngagement?.engagementProcess?.buyers || [];
        const ndaStatusMap = new Map(
            engagementBuyers.map((buyer) => [
                buyer.buyerId,
                buyer.isDataNdaSigned,
            ])
        );

        return baseBuyers.filter((buyer) => {
            if (!buyer.isActive) return false;
            const hasSignedNda = ndaStatusMap.has(buyer.buyerId)
                ? ndaStatusMap.get(buyer.buyerId)
                : buyer.isDataNdaSigned;
            return hasSignedNda;
        });
    }, [currentEngagement, props.selectedBuyers]);

    const getBuyerName = (event: MeetingObject): string => {
        const buyerId = event.buyerId ?? event.posterId;
        const buyer = activeBuyers?.find(
            (b) => Number(b.buyerId) === Number(buyerId)
        );
        return (
            buyer?.companyName ||
            buyer?.buyerAccountName ||
            event.posterName ||
            `Buyer ${buyerId}`
        );
    };

    const timeSlots = useMemo<TimeSlot[]>(() => {
        const slots: TimeSlot[] = [];
        for (let hour = 0; hour < TOTAL_HOURS; hour++) {
            const isPM = hour >= 12;
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            const period = isPM ? 'PM' : 'AM';
            slots.push({ hour, label: `${displayHour} ${period}` });
        }
        return slots;
    }, []);

    const sortedEvents = useMemo(() => {
        return [...props.events].sort((a, b) => {
            const aIsBlock = isAvailabilityBlockObject(a);
            const bIsBlock = isAvailabilityBlockObject(b);
            const aIsOtherGhost = isOtherGhostMeetingObject(a);
            const bIsOtherGhost = isOtherGhostMeetingObject(b);

            if (aIsBlock && !bIsBlock) return -1;
            if (!aIsBlock && bIsBlock) return 1;
            if (aIsOtherGhost && !bIsOtherGhost && !bIsBlock) return -1;
            if (!aIsOtherGhost && bIsOtherGhost && !aIsBlock) return 1;

            return moment(a.dateStart).diff(moment(b.dateStart));
        });
    }, [props.events]);

    const { eventCol, totalColumns } = useMemo(
        () => computeColumnLayout(props.events),
        [props.events]
    );

    useEffect(() => {
        if (!scrollContainerRef.current) return;

        const now = moment();
        const currentTimePos =
            now.hours() * HOUR_HEIGHT_PX +
            (now.minutes() / 60) * HOUR_HEIGHT_PX;
        const containerHeight = scrollContainerRef.current.clientHeight;
        const scrollPosition = currentTimePos - containerHeight / 2;
        const maxScroll =
            scrollContainerRef.current.scrollHeight - containerHeight;
        const clampedScroll = Math.max(0, Math.min(scrollPosition, maxScroll));

        scrollContainerRef.current.scrollTo({
            top: clampedScroll,
            behavior: 'smooth',
        });
    }, []);

    const handleEventClick = (event: CalendarObject) => {
        props.setSelectedEvent?.(event);
    };

    const navigateToDocumentDiscussion = async (
        commsId: number,
        discussionId: number
    ) => {
        const commsHubTab =
            party === TimelineParty.Buyer
                ? BuyerOppMarketplaceTab.CommsHub
                : SellerMarketplaceTab.CommsHub;

        if (party === TimelineParty.Buyer) {
            navigate(
                `/opportunities?opportunityId=${props.engagementId}&tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${commsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
            );
        } else {
            navigate(
                `/marketplace/engagement/${props.engagementId}?tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${commsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
            );
        }
    };

    const getEventFormatString = (event: CalendarObject): string => {
        if (isAvailabilityBlockObject(event)) {
            return (
                event.formats
                    ?.map((format) => MeetingFormatTypesOptions[format.format])
                    .join(', ') || ''
            );
        }
        const meeting = event as MeetingObject;
        return meeting.format ? MeetingFormatTypesOptions[meeting.format] : '';
    };

    const getEventClassName = (event: CalendarObject): string => {
        const isAvailabilityBlock = isAvailabilityBlockObject(event);
        const isSelected = isAvailabilityBlock
            ? (props.selectedEvent as AvailabilityBlockObject)
                  ?.availabilityBlockId ===
              (event as AvailabilityBlockObject).availabilityBlockId
            : props.selectedEvent?.id === event.id;
        const isHighlighted =
            !isAvailabilityBlock && props.highlightedMeetingId === event.id;

        let className = 'event-block';

        if (isAvailabilityBlockObject(event)) {
            className += ' availability-block';
        } else if (isOtherGhostMeetingObject(event)) {
            className += ' other-ghost-meeting';
        }
        if (isSelected) className += ' selected';
        if (isHighlighted) className += ' highlighted';

        if (!isAvailabilityBlockObject(event)) {
            const meeting = event as MeetingObject;
            const isUnconfirmedProposal =
                (meeting.isMeetingProposal === true ||
                    meeting.proposalStatus != null) &&
                meeting.proposalStatus !== MeetingProposalStatus.Confirmed;

            if (meeting.isCancelled) {
                className += ' proposal-cancelled';
            } else if (isUnconfirmedProposal) {
                className += ' proposal-pending';
            }

            if (
                meeting.participantsType &&
                props.viewMode !== 'buyerTopLevel'
            ) {
                if (meeting.engagementId !== props.engagementId) {
                    className += ' other-deals';
                }

                if (!isUnconfirmedProposal) {
                    switch (meeting.participantsType) {
                        case MeetingParticipantsType.SellerBuyer:
                            className += ' seller-buyer';
                            break;
                        case MeetingParticipantsType.SellerAdvisor:
                            className += ' seller-advisor';
                            break;
                        case MeetingParticipantsType.SellerAdminSupport:
                            className += ' seller-admin-support';
                            break;
                        case MeetingParticipantsType.BuyerAdminSupport:
                            className += ' buyer-admin-support';
                            break;
                    }
                }
            }
        }

        return className;
    };

    const mapColorCodeStyle = (format: MeetingFormat): string => {
        switch (format) {
            case MeetingFormat.InPerson:
                return 'color-white';
            case MeetingFormat.Virtual:
                return 'color-black';
            case MeetingFormat.Mixed:
                return 'color-gray';
            default:
                return '';
        }
    };

    const mapMeetingTile = (
        event: MeetingObject,
        eventStart: moment.Moment,
        eventEnd: moment.Moment,
        top: number,
        height: number,
        widthPercent: number,
        leftPercent: number
    ): JSX.Element | null => {
        let titleString =
            props.party === TimelineParty.Buyer
                ? event.engagementId
                    ? `[OppId ${event.engagementId}]`
                    : `${event.name}`
                : event.name;

        const buyerName = getBuyerName(event);
        if (titleString === '') {
            titleString = `Meeting with ${buyerName}`;
        }

        const timeRange = `${eventStart.format('h:mm A')} - ${eventEnd.format('h:mm A')}`;
        const tooltipText = `${titleString}\n${timeRange}`;

        const shouldShowLocation = event.format !== MeetingFormat.Virtual;
        let locationText: string | null = null;
        if (shouldShowLocation) {
            if (event.address) {
                locationText = `${event.address.city || 'N/A'}, ${event.address.state || 'N/A'}`;
            } else {
                locationText = 'N/A';
            }
        }

        const isSelected = props.selectedEvent?.id === event.id;
        const MIN_EXPANDED_HEIGHT = 120;
        const shouldExpand = isSelected && height < MIN_EXPANDED_HEIGHT;
        const displayHeight = shouldExpand ? MIN_EXPANDED_HEIGHT : height;

        return (
            <div
                key={event.id}
                id={`event-${event.id}`}
                className={getEventClassName(event)}
                style={{
                    position: 'absolute',
                    top: `${top}px`,
                    height: `${displayHeight}px`,
                    left: `${leftPercent}px`,
                    width: `${widthPercent}px`,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    handleEventClick(event);
                }}
                title={tooltipText}
                data-tooltip-title={titleString}
                data-tooltip-time={timeRange}>
                {props.getNotificationFlagCodeForEvent &&
                    (() => {
                        const flagCode =
                            props.getNotificationFlagCodeForEvent(event);
                        return flagCode ? (
                            <div
                                className="week-view-event-notification-flag"
                                onClick={(e) => e.stopPropagation()}>
                                <NotificationFlag
                                    flagClassName="nf-flag"
                                    eventCode={flagCode}
                                    includePrefix={true}
                                    deletePrefixMatches={true}
                                />
                            </div>
                        ) : null;
                    })()}
                <div
                    className={`event-content${
                        !props.viewMode.endsWith('TopLevel') &&
                        !event.isOtherGhostMeeting
                            ? ' pr-30'
                            : ''
                    }`}>
                    <div className="event-content-row">
                        <div className="event-title">{titleString}</div>
                        <div
                            className={`event-format ${mapColorCodeStyle(event.format)}`}>
                            {getEventFormatString(event)}
                        </div>
                    </div>
                    <div className="event-content-row">
                        {shouldShowLocation && (
                            <div
                                className={`event-location ${mapColorCodeStyle(event.format)}`}>
                                {locationText}
                            </div>
                        )}
                        <div className="event-time">
                            {eventStart.format('h:mm A')} -{' '}
                            {eventEnd.format('h:mm A')}
                        </div>
                    </div>

                    {event.isCancelled && (
                        <div className="event-cancelled-label">Cancelled</div>
                    )}

                    {(() => {
                            const inPersonList =
                                event.inPersonMeetingParticipants || [];
                            const virtualList = event.participants || [];

                            type ParticipantEntry = {
                                name: string;
                                isInPerson: boolean;
                            };
                            const sellerParticipants: ParticipantEntry[] = [];
                            const buyerParticipants: ParticipantEntry[] = [];

                            inPersonList.forEach((p) => {
                                const entry: ParticipantEntry = {
                                    name: p.name,
                                    isInPerson: true,
                                };
                                if (p.party === TimelineParty.Seller)
                                    sellerParticipants.push(entry);
                                else if (p.party === TimelineParty.Buyer)
                                    buyerParticipants.push(entry);
                            });

                            virtualList.forEach((p) => {
                                const entry: ParticipantEntry = {
                                    name: p.name,
                                    isInPerson: false,
                                };
                                if (p.engagementRole === TimelineParty.Seller)
                                    sellerParticipants.push(entry);
                                else if (
                                    p.engagementRole === TimelineParty.Buyer
                                )
                                    buyerParticipants.push(entry);
                            });

                            if (
                                sellerParticipants.length === 0 &&
                                buyerParticipants.length === 0
                            ) {
                                return null;
                            }

                            const showBadge =
                                event.format === MeetingFormat.Mixed;
                            return (
                                <div className="event-participants">
                                    <div className="participants-column">
                                        <div className="participants-column-header">
                                            {party !== TimelineParty.Buyer
                                                ? 'Our Attendees'
                                                : props.practiceName ||
                                                  'Seller Attendees'}
                                        </div>
                                        {sellerParticipants.map((p, i) => {
                                            const badge = `(${p.isInPerson ? 'IP' : 'V'})`;
                                            return (
                                                <div
                                                    key={i}
                                                    className="participant-entry"
                                                    title={
                                                        p.name +
                                                        (showBadge
                                                            ? ' ' + badge
                                                            : '')
                                                    }>
                                                    <span className="participant-name">
                                                        {p.name}
                                                    </span>
                                                    {showBadge && (
                                                        <span
                                                            className={`participant-badge ${p.isInPerson ? 'white' : 'black'}`}>
                                                            {badge}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="participants-column">
                                        <div className="participants-column-header">
                                            {party !== TimelineParty.Buyer
                                                ? buyerName || 'Buyer Attendees'
                                                : 'Our Attendees'}
                                        </div>
                                        {buyerParticipants.map((p, i) => {
                                            const badge = `(${p.isInPerson ? 'IP' : 'V'})`;
                                            return (
                                                <div
                                                    key={i}
                                                    className="participant-entry"
                                                    title={
                                                        p.name +
                                                        (showBadge
                                                            ? ' ' + badge
                                                            : '')
                                                    }>
                                                    <span className="participant-name">
                                                        {p.name}
                                                    </span>
                                                    {showBadge && (
                                                        <span
                                                            className={`participant-badge ${p.isInPerson ? 'white' : 'black'}`}>
                                                            {badge}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                    {!event.isOtherGhostMeeting && (
                        <button
                            className="event-full-info-link"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMeetingForFullInfo(event);
                            }}>
                            Show full info
                        </button>
                    )}
                </div>
                {!props.viewMode.endsWith('TopLevel') &&
                    !event.isOtherGhostMeeting &&
                    (() => {
                        const isMeetingProposal =
                            event.isMeetingProposal === true;
                        const proposalStatus = event.proposalStatus;
                        const currentParty = props.party;

                        const shouldShowProposalActions =
                            isMeetingProposal &&
                            !event.isCancelled &&
                            ((currentParty === TimelineParty.Seller &&
                                proposalStatus ===
                                    MeetingProposalStatus.PendingSellerAnswer) ||
                                (currentParty === TimelineParty.Buyer &&
                                    proposalStatus ===
                                        MeetingProposalStatus.PendingBuyerAnswer));

                        if (shouldShowProposalActions) {
                            const canManage =
                                canBuyerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                ) ||
                                canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                );
                            const isSellerTMWithoutCommsHub =
                                isSellerTeamMemberForEngagement(
                                    summary,
                                    props.engagementId
                                ) &&
                                !canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                );
                            return (
                                <div
                                    className="event-actions proposal-actions"
                                    onClick={(e) => e.stopPropagation()}>
                                    {!isSellerTMWithoutCommsHub && (
                                        <button
                                            className="event-action-btn"
                                            onClick={() => {
                                                if (event.meetingProposalId) {
                                                    setPendingProposalId(
                                                        event.meetingProposalId
                                                    );
                                                    setShowApproveConfirmation(
                                                        true
                                                    );
                                                }
                                            }}
                                            title="Accept Proposal">
                                            <FontAwesomeIcon icon={faCheck} />
                                        </button>
                                    )}
                                    {canManage && (
                                        <button
                                            className="event-action-btn proposal-reject-btn"
                                            onClick={() => {
                                                if (event.meetingProposalId) {
                                                    setPendingProposalId(
                                                        event.meetingProposalId
                                                    );
                                                    setShowCancelConfirmation(
                                                        true
                                                    );
                                                }
                                            }}
                                            title="Reject Proposal">
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                    {canManage &&
                                        !(
                                            isSellerTeamMemberForEngagement(
                                                summary,
                                                props.engagementId
                                            ) &&
                                            proposalStatus ===
                                                MeetingProposalStatus.PendingBuyerAnswer
                                        ) && (
                                            <button
                                                className="event-action-btn"
                                                onClick={() => {
                                                    props.onEditClick?.(
                                                        event.meetingProposalId !=
                                                            null
                                                            ? event.meetingProposalId
                                                            : event.id
                                                    );
                                                    props.onClose();
                                                }}
                                                title="Edit Proposal">
                                                <FontAwesomeIcon
                                                    icon={faEdit}
                                                />
                                            </button>
                                        )}
                                </div>
                            );
                        }

                        if (event.isCancelled) {
                            return <></>;
                        }

                        const shouldNotShowProposalActions =
                            isMeetingProposal &&
                            ((currentParty !== TimelineParty.Seller &&
                                proposalStatus ===
                                    MeetingProposalStatus.PendingSellerAnswer) ||
                                (currentParty !== TimelineParty.Buyer &&
                                    proposalStatus ===
                                        MeetingProposalStatus.PendingBuyerAnswer));

                        if (shouldNotShowProposalActions) {
                            return <></>;
                        }

                        return (
                            <div
                                className="event-actions"
                                onClick={(e) => e.stopPropagation()}>
                                <button
                                    className="event-action-btn"
                                    onClick={() =>
                                        navigateToDocumentDiscussion(
                                            event.commsId,
                                            event.threadedDiscussionId
                                        )
                                    }
                                    title="Meeting Discussion">
                                    <FontAwesomeIcon icon={faCommentDots} />
                                </button>
                                {!(isAdmin || isBroker) &&
                                    proposalStatus !==
                                        MeetingProposalStatus.Confirmed &&
                                    (canBuyerTeamMemberManageMeetings(
                                        summary,
                                        props.engagementId
                                    ) ||
                                        canSellerTeamMemberManageMeetings(
                                            summary,
                                            props.engagementId
                                        )) && (
                                        <button
                                            className="event-action-btn"
                                            onClick={() => {
                                                props.onEditClick?.(
                                                    (event as any)
                                                        .meetingProposalId !=
                                                        null
                                                        ? (event as any)
                                                              .meetingProposalId
                                                        : event.id
                                                );
                                                props.onClose();
                                            }}
                                            title="Edit">
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                    )}
                                <button
                                        className="event-action-btn"
                                        onClick={() => {
                                            setSelectedMeetingForParticipants(
                                                event
                                            );
                                            setIsParticipantsModalVisible(true);
                                        }}
                                        title="Manage Attendees">
                                        <FontAwesomeIcon icon={faUser} />
                                    </button>
                                {!(isAdmin || isBroker) &&
                                    proposalStatus ===
                                        MeetingProposalStatus.Confirmed && (
                                        <button
                                            className="event-action-btn"
                                            onClick={() => {
                                                navigate(
                                                    `/marketplace/meetings?engagementId=${props.engagementId}&meetingId=${event.id}`
                                                );
                                            }}
                                            title="Go to scheduled meetings tab">
                                            <FontAwesomeIcon
                                                icon={faCalendar}
                                            />
                                        </button>
                                    )}
                                {!(isAdmin || isBroker) &&
                                    (canBuyerTeamMemberManageMeetings(
                                        summary,
                                        props.engagementId
                                    ) ||
                                        canSellerTeamMemberManageMeetings(
                                            summary,
                                            props.engagementId
                                        )) &&
                                    !hasMeetingStarted(event) && (
                                        <button
                                            className="event-action-btn proposal-reject-btn"
                                            onClick={() => {
                                                if (event.meetingProposalId) {
                                                    setPendingProposalId(
                                                        event.meetingProposalId
                                                    );
                                                    setShowCancelConfirmation(
                                                        true
                                                    );
                                                }
                                            }}
                                            title="Cancel Meeting">
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                            </div>
                        );
                    })()}
            </div>
        );
    };

    const renderEvent = (event: CalendarObject): JSX.Element | null => {
        const midnight = moment(props.date).startOf('day');
        const eventStart = moment(event.dateStart);
        const eventEnd = moment(event.dateEnd);

        const startMinutesFromMidnight = eventStart.diff(midnight, 'minutes');
        const durationMinutes = eventEnd.diff(eventStart, 'minutes');

        const top = (startMinutesFromMidnight / 60) * HOUR_HEIGHT_PX;
        const height = (durationMinutes / 60) * HOUR_HEIGHT_PX;

        const widthPercent = 400;
        const leftPercent = 8;

        if (isAvailabilityBlockObject(event)) {
            return null;
        } else {
            return mapMeetingTile(
                event as MeetingObject,
                eventStart,
                eventEnd,
                top,
                height,
                widthPercent,
                leftPercent
            );
        }
    };

    const anySubModalOpen =
        showApproveConfirmation ||
        showCancelConfirmation ||
        selectedMeetingForFullInfo !== null ||
        isParticipantsModalVisible;

    return (
        <>
            <Rodal
                width={1200}
                height={700}
                visible={props.isVisible && !anySubModalOpen}
                animation="slideDown"
                onClose={props.onClose}
                center
                className="overlapping-day-calendar-rodal">
                <ModalHeader>
                    Meetings on {moment(props.date).format('dddd, MMM D')}
                </ModalHeader>
                <ModalBody className="overlapping-day-calendar-body">
                    <div className="overlapping-day-calendar-view">
                        <div
                            className="odc-scroll-container"
                            ref={scrollContainerRef}>
                            <div className="odc-inner">
                                <div className="odc-time-labels">
                                    {timeSlots.map((slot) => (
                                        <div
                                            key={slot.hour}
                                            className="odc-time-label">
                                            {slot.label}
                                        </div>
                                    ))}
                                </div>
                                <div className="odc-columns-wrapper">
                                    {Array.from(
                                        { length: totalColumns },
                                        (_, colIdx) => {
                                            const eventsForColumn =
                                                sortedEvents.filter((e) =>
                                                    isAvailabilityBlockObject(e)
                                                        ? colIdx === 0
                                                        : eventCol.get(e) ===
                                                          colIdx
                                                );

                                            return (
                                                <div
                                                    key={colIdx}
                                                    className={`odc-column${colIdx > 0 ? ' odc-column--bordered' : ''}`}>
                                                    {eventsForColumn.map(
                                                        (event, eventIdx) => {
                                                            const uniqueKey = `col-${colIdx}-event-${eventIdx}-${moment(event.dateStart).format('HHmm')}`;
                                                            const renderedEvent =
                                                                renderEvent(
                                                                    event
                                                                );
                                                            // Clone element to set unique key
                                                            return renderedEvent
                                                                ? React.cloneElement(
                                                                      renderedEvent,
                                                                      {
                                                                          key: uniqueKey,
                                                                      }
                                                                  )
                                                                : null;
                                                        }
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </Rodal>

            {showApproveConfirmation && (
                <ConfirmationModal
                    isVisible={showApproveConfirmation}
                    setIsVisible={setShowApproveConfirmation}
                    header="Approve Meeting Proposal"
                    confirmationText="Are you sure you want to approve this meeting proposal?"
                    onConfirm={async () => {
                        if (pendingProposalId && props.onProposalAccept) {
                            setIsApproving(true);
                            try {
                                await props.onProposalAccept(pendingProposalId);
                                setShowApproveConfirmation(false);
                                setPendingProposalId(undefined);
                            } catch (error) {
                            } finally {
                                setIsApproving(false);
                            }
                        }
                    }}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isApproving}
                />
            )}

            {showCancelConfirmation && (
                <ConfirmationModal
                    isVisible={showCancelConfirmation}
                    setIsVisible={setShowCancelConfirmation}
                    header="Cancel Meeting Proposal"
                    confirmationText="Are you sure you want to cancel this meeting proposal?"
                    onConfirm={async () => {
                        if (pendingProposalId && props.onProposalCancel) {
                            setIsCancelling(true);
                            try {
                                await props.onProposalCancel(pendingProposalId);
                                setShowCancelConfirmation(false);
                                setPendingProposalId(undefined);
                                props.onRefreshMeetings?.();
                            } catch (error) {
                            } finally {
                                setIsCancelling(false);
                            }
                        }
                    }}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isCancelling}
                />
            )}

            {selectedMeetingForParticipants &&
                props.engagementId &&
                selectedMeetingForParticipants.meetingProposalId && (
                    <InPersonMeetingParticipantsModal
                        isVisible={isParticipantsModalVisible}
                        onClose={() => {
                            setIsParticipantsModalVisible(false);
                            setSelectedMeetingForParticipants(null);
                            if (props.onRefreshMeetings) {
                                props.onRefreshMeetings();
                            }
                        }}
                        engagementId={props.engagementId}
                        meetingId={
                            selectedMeetingForParticipants.meetingProposalId
                        }
                        meetingFormat={selectedMeetingForParticipants.format}
                        meetingStartDate={
                            selectedMeetingForParticipants.dateStart
                        }
                        meetingStatus={selectedMeetingForParticipants.status}
                        isReadOnly={
                            isEngagementLocked ||
                            props.party === TimelineParty.VetValue ||
                            props.party === TimelineParty.Broker ||
                            !(
                                canBuyerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                ) ||
                                canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                )
                            ) ||
                            ((selectedMeetingForParticipants.status ===
                                EngagementMeetingStatus.InProgress ||
                                selectedMeetingForParticipants.status ===
                                    EngagementMeetingStatus.Completed) &&
                                !canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                ))
                        }
                    />
                )}

            {selectedMeetingForFullInfo && (
                <EngagementMeetingFullInfoModal
                    isVisible={true}
                    onClose={() => setSelectedMeetingForFullInfo(null)}
                    event={selectedMeetingForFullInfo}
                    party={party}
                    buyerName={getBuyerName(selectedMeetingForFullInfo)}
                    practiceName={props.practiceName}
                />
            )}
        </>
    );
};
